import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Calendar, MapPin, ExternalLink, Search, Briefcase } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { SEOHead } from './SEOHead';
import { PageFaq } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema } from '../services/SiteSettings';
import {
  getPublicProjects,
  searchProjects,
  ProjectPayload,
} from "../services/Project";
import { htmlToText } from "../services/richText";
import { projectPath } from "./ProjectDetailsPage";

export function ProjectsPage() {
  const settings = useSiteSettings();
  const seo = getPageSeo(settings, '/projects');
  const faqSchema = buildFaqSchema(seo.faqs);
  const [projects, setProjects] = useState<ProjectPayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const sortProjects = (data: ProjectPayload[]) => {
    return data.sort((a, b) => {
      // Extract year and month with better month range handling
      const extractDateInfo = (yearText: string | undefined): { year: number; month: number } => {
        if (!yearText) return { year: 0, month: 0 };

        // Try to find 4-digit year in the text
        const yearMatch = yearText.match(/\b(20\d{2})\b/);
        const year = yearMatch ? parseInt(yearMatch[1], 10) : 0;

        // Extract month information - use the later month in range for sorting
        const monthText = yearText.toLowerCase();
        let month = 0;

        // Month mapping
        const monthMap: { [key: string]: number } = {
          'january': 1, 'jan': 1,
          'february': 2, 'feb': 2,
          'march': 3, 'mar': 3,
          'april': 4, 'apr': 4,
          'may': 5,
          'june': 6, 'jun': 6,
          'july': 7, 'jul': 7,
          'august': 8, 'aug': 8,
          'september': 9, 'sep': 9,
          'october': 10, 'oct': 10,
          'november': 11, 'nov': 11,
          'december': 12, 'dec': 12
        };

        // Check for all months in the text and take the highest month value
        Object.keys(monthMap).forEach(monthName => {
          if (monthText.includes(monthName)) {
            month = Math.max(month, monthMap[monthName]);
          }
        });

        return { year, month };
      };

      const dateA = extractDateInfo(a.year);
      const dateB = extractDateInfo(b.year);

      // Handle cases where date might be missing or invalid
      if (dateA.year === 0 && dateB.year === 0) return 0;
      if (dateA.year === 0) return 1; // projects without date go to end
      if (dateB.year === 0) return -1; // projects with date come first

      // First compare by year (descending)
      if (dateA.year !== dateB.year) {
        return dateB.year - dateA.year;
      }

      // If same year, compare by month (descending)
      if (dateA.month !== dateB.month) {
        return dateB.month - dateA.month;
      }

      // If same year and month, maintain original order
      return 0;
    });
  };

  const fetchProjects = async () => {
    // Show the cached list right away, then always refresh so newly added projects appear
    const storedProjects = sessionStorage.getItem('publicProjects');
    if (storedProjects) {
      setProjects(sortProjects(JSON.parse(storedProjects)));
      setError(null);
    } else {
      setLoading(true);
    }

    try {
      const data = await getPublicProjects();
      sessionStorage.setItem('publicProjects', JSON.stringify(data));
      setProjects(sortProjects(data));
      setError(null);
    } catch (err: any) {
      if (!storedProjects) setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      fetchProjects();
      return;
    }
    try {
      setSearching(true);

      // Check if we have projects in session storage to search from
      const storedProjects = sessionStorage.getItem('publicProjects');

      if (storedProjects) {
        // Search from stored projects
        const allProjects = JSON.parse(storedProjects);
        const filteredData = allProjects.filter((project: ProjectPayload) =>
          project.title?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          htmlToText(project.description).toLowerCase().includes(searchKeyword.toLowerCase()) ||
          project.category?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          project.location?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          project.typeOfAssignment?.toLowerCase().includes(searchKeyword.toLowerCase()) // Added search for typeOfAssignment
        );
        const sortedData = sortProjects(filteredData);
        setProjects(sortedData);
      } else {
        // Use API search if no stored projects
        const data = await searchProjects(searchKeyword.trim());
        const sortedData = sortProjects(data);
        setProjects(sortedData);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Water Resources":
        return "bg-blue-500";
      case "Social Research":
        return "bg-purple-500";
      case "Environmental Assessment":
        return "bg-green-500";
      case "Data Collection":
        return "bg-orange-500";
      default:
        return "bg-gray-500";
    }
  };

  const truncateText = (text: string, length: number) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.slice(0, length) + "...";
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://uspconsultancy.com.np/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Projects",
        "item": "https://uspconsultancy.com.np/projects"
      }
    ]
  };

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Projects'}
        description={seo.description}
        canonical="https://uspconsultancy.com.np/projects"
        keywords={seo.keywords}
        takeaways={seo.takeaways}
        structuredData={faqSchema ? [breadcrumbSchema, faqSchema] : breadcrumbSchema}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="mb-4">Our Completed Projects</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Explore our portfolio of successful projects that have made a
            positive impact on communities and organizations across Nepal.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-12 flex justify-center">
          <div className="relative w-full max-w-md">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              className="w-full border border-gray-300 rounded-lg py-2 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 inset-y-0 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Loading & Error */}
        {(loading || searching) && (
          <div className="text-center py-16">Loading projects...</div>
        )}
        {error && <div className="text-center py-16 text-red-500">{error}</div>}

        {/* Projects Grid */}
        {!loading && !searching && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-16">
                No projects found.
              </div>
            ) : (
              projects.map((project, index) => (
                <Card
                  key={index}
                  className="overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="relative w-full h-full overflow-hidden">
                    {/* Show image only if imageUrl exists */}
                    {project.imageUrl && (
                      <ImageWithFallback
                        src={project.imageUrl}
                        alt={project.title}
                        className="w-full h-full object-contain"
                      />
                    )}
                    <div className="absolute top-4 left-4">
                      <div
                        className={`w-3 h-3 rounded-full ${getCategoryColor(
                          project.category
                        )}`}
                      ></div>
                    </div>
                    {/* <div className="absolute top-4 right-4">
                      <Badge className={getStatusColor(project.status)}>
                        {project.status}
                      </Badge>
                    </div> */}
                  </div>

                  <CardHeader>
                    <CardTitle className="text-lg leading-tight">
                      {project.title}
                    </CardTitle>
                    <div className="flex flex-col space-y-2 text-sm text-gray-500 mt-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{project.year}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{project.location}</span>
                      </div>
                      {/* Type of Assignment - New Section */}
                      {project.typeOfAssignment && (
                        <div className="flex items-center space-x-1">
                          <Briefcase className="w-4 h-4" />
                          <span className="text-xs">{project.typeOfAssignment}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent>
                    <p className="text-gray-600 mb-4 text-sm">
                      {truncateText(htmlToText(project.description), 120)}
                    </p>

                    <div className="mb-4 flex flex-wrap gap-2">
                      <Badge variant="secondary" className="mb-2">
                        {project.category}
                      </Badge>
                      {/* Type of Assignment Badge - Alternative display */}
                      {project.typeOfAssignment && (
                        <Badge variant="outline" className="text-xs">
                          {project.typeOfAssignment}
                        </Badge>
                      )}
                    </div>

                    {/* {project.tags?.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <h4 className="text-sm">Key tags:</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {project.tags.map((tag, idx) => (
                            <li
                              key={idx}
                              className="flex items-start space-x-2"
                            >
                              <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                              <span>{tag}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )} */}

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full cursor-pointer"
                      asChild
                    >
                      <a
                        href={projectPath(project as any)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View details of ${project.title} (opens in a new tab)`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center bg-gray-50 rounded-lg p-8">
          <h2 className="mb-4">Have a Project in Mind?</h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Let's discuss how our expertise can help bring your vision to life.
            Contact us to explore potential collaboration opportunities.
          </p>
          <Button size="lg" asChild>
            <a href="/contact">Start a Conversation</a>
          </Button>
        </div>
      </div>

      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}